import React, { useId, useEffect, useState, useCallback } from 'react'
import { Alert, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import SearchField from '../Forms/SearchField'
import Asset from './Asset'
import LazyLoad from '../Forms/LazyLoad'
import SearchResultAmount from '../Forms/SearchResultAmount'
import { makeCall } from '../../utils/api/makeCall'

const AssetSelect = ({
    selectedAssets,
    setSelectedAssets,
    hideAssets,
    listRef,
    singleSelection,
}) => {
    const [fetchedAssets, setFetchedAssets] = useState([])
    const [searchText, setSearchText] = useState('')
    const [offset, setOffset] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const { t } = useTranslation()
    const { createOrCancelToken, sourceRef, isCancel } = useCancelToken()
    const limit = 10
    const [lastClickedAssetId, setLastClickedAssetId] = useState(null)
    const [allowRange, setAllowRange] = useState(false)
    const [allSelected, setAllSelected] = useState(false)
    // here we are checking if there are already selected assets
    const filteredArray = hideAssets
        ? fetchedAssets.filter(
            (item) => !hideAssets.find((element) => element === item.id)
        )
        : fetchedAssets
    const [, setErrorStates] = useState({})
    const [loadingStates, setLoadingStates] = useState({})
    const [startWithSelectAll, setStartWithSelectAll] = useState(false)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const handleChangeSearchText = async (newSearchText) => {
        setAllSelected(false)
        setSelectedAssets([])
        setLastClickedAssetId(null)
        setSearchText(newSearchText)
        fetchAssets({ newOffset: 0, newSearch: newSearchText })
    }

    const fetchAssets = async (
        newParams = { newOffset: offset, newSearch: searchText }
    ) => {
        createOrCancelToken()
        let append = !!newParams.newOffset
        const params = getParamsForAssetsRequest(newParams)
        makeCall(
            'getAssets',
            async () => {
                let response = await apiClient.getJson(
                    APIEndpoints.assets(),
                    params,
                    true,
                    true,
                    true,
                    { cancelToken: sourceRef.current.token }
                )
                setFetchedAssets((prev) =>
                    append ? [...prev, ...response.data] : response.data
                )
                const total = parseInt(response.headers['x-total-count'])
                if (!isNaN(total)) {
                    setTotalCount(total)
                }
                setOffset(newParams.newOffset + limit)
            },
            setErrorStates,
            setLoadingStates,
            isCancel
        )
    }

    const getParamsForAssetsRequest = (newParams) => {
        const { newOffset, newSearch } = newParams
        return {
            'filter.searchText': newSearch ? newSearch : null,
            'page.offset': newOffset,
            'page.limit': limit,
        }
    }

    function getAssetsInRange(lastClickedId, newId) {
        let lastIndex = filteredArray.findIndex(
            (asset) => asset.id === lastClickedId
        )
        let newIndex = filteredArray.findIndex(
            (asset) => asset.id === newId
        )
        if (lastIndex < newIndex) {
            return filteredArray.slice(lastIndex, newIndex + 1)
        } else if (lastIndex > newIndex) {
            return filteredArray.slice(newIndex, lastIndex + 1)
        } else return []
    }

    const onClickSelectedAssets = (asset) => {
        setAllSelected(false)
        let selectedAssetId = asset.id
        setLastClickedAssetId(selectedAssetId)
        let newSelectedAssets = [...selectedAssets]
        let existingAssetIndex = selectedAssets.findIndex(
            (asset) => asset.id === selectedAssetId
        )
        if (allowRange) {
            let assetsInRange = getAssetsInRange(
                lastClickedAssetId,
                selectedAssetId
            )
            //lastClickedAssetId is selected atm
            if (
                selectedAssets.find(
                    (asset) => asset.id === lastClickedAssetId
                )
            ) {
                //add assets
                setSelectedAssets([
                    ...new Set([...newSelectedAssets, ...assetsInRange]),
                ])
            }
        } else if (existingAssetIndex >= 0) {
            //remove asset if already selected
            newSelectedAssets.splice(existingAssetIndex, 1)
            setSelectedAssets(newSelectedAssets)
        } else {
            if (singleSelection) newSelectedAssets = []
            newSelectedAssets.push(asset)
            setSelectedAssets(newSelectedAssets)
        }
    }

    useEffect(() => {
        let listRefCurrent = listRef?.current
        const keypressShiftCallback = (e) => {
            if (!singleSelection && e.shiftKey && lastClickedAssetId) {
                setAllowRange(true)
            } else {
                setAllowRange(false)
            }
        }
        if (listRefCurrent) {
            listRefCurrent.addEventListener('click', keypressShiftCallback)
        }
        return () => {
            listRefCurrent &&
                listRefCurrent.removeEventListener('click', keypressShiftCallback)
        }
    }, [lastClickedAssetId, listRef, singleSelection])

    const setAllAssets = useCallback(async () => {
        var assets = []
        const params = {
            'filter.searchText': searchText,
        }
        await makeCall(
            'getAllAssets',
            async () => {
                assets = await apiClient.getJson(APIEndpoints.assets(), params)
            },
            setErrorStates,
            setLoadingStates,
            isCancel
        )
        return assets
    }, [
        searchText,
        isCancel,
    ])

    const selectAll = useCallback(async () => {
        var assets = []
        if (!allSelected) {
            assets = await setAllAssets()
        }
        setSelectedAssets(assets)
        setAllSelected((prev) => !prev)
    }, [allSelected, setSelectedAssets, setAllSelected, setAllAssets])
    useEffect(() => {
        if (startWithSelectAll && searchText) {
            setStartWithSelectAll(false)
            selectAll()
        }
    }, [startWithSelectAll, searchText, selectAll])
    const checkboxId = useId()
    return (
        <>
            {windowWidth >= 476 && (
                <Form.Group className='mb-3 p-2'>
                    <Form.Label>{t('inventory:searchAsset')}</Form.Label>
                    <SearchField
                        placeholder={t('inventory:searchAsset')}
                        value={searchText}
                        onChange={(e) => handleChangeSearchText(e.target.value)}
                        onClearClick={() => handleChangeSearchText('')}
                    />
                    <div className='d-flex justify-content-between'>
                        {/* LEFT: Result amount only if valid */}
                        {searchText && typeof totalCount === 'number' && totalCount > 0 ? (
                            <SearchResultAmount count={totalCount} />
                        ) : (
                            <div className='mt-2'>&nbsp;</div>
                        )}

                        {/* RIGHT: Spinner or checkbox */}
                        {loadingStates.getAllAssets ? (
                            <Spinner
                                size='sm'
                                className='mt-2'
                                animation='border'
                                variant='primary'
                            />
                        ) : (
                            filteredArray.length > 0 &&
                            !singleSelection && (
                                <Form.Check
                                    className='mt-2'
                                    label={t('order:selectAll')}
                                    id={checkboxId}
                                    checked={allSelected}
                                    onChange={selectAll}
                                />
                            )
                        )}
                    </div>
                </Form.Group>
            )}
            {filteredArray.map((asset, index) =>
                <Asset
                    key={asset.id}
                    onClickSelectedAssets={onClickSelectedAssets}
                    selectedAssets={selectedAssets}
                    asset={asset}
                    index={index}
                />
            )}
            {searchText && !loadingStates.getAssets && !filteredArray.length && (
                <>
                    <Alert variant='info'>
                        {t('addAssetTable:noAssetAvailable')}
                    </Alert>
                </>
            )}
            {totalCount > offset && !loadingStates.getAssets ? (
                <LazyLoad fetchData={fetchAssets} listRef={listRef} />
            ) : (
                ''
            )}
            {loadingStates.getAssets && (
                <div className='d-flex justify-content-center p-3'>
                    <Spinner animation='border' variant='primary' />
                </div>
            )}
        </>
    )
}

export default AssetSelect
